import { useCallback, useEffect, useState, useRef, useContext, useMemo } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { MultiSelect } from "primereact/multiselect";
import { Paginator } from "primereact/paginator";
import { BreadCrumb } from "primereact/breadcrumb";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Menu } from "primereact/menu";
import SourceForm from "./SourceForm";
import { getSources, updateSource, toggleSource, createSource, deleteSource } from "../../api/sourceApi";
import { getProducts } from "../../api/productApi";
import { AuthContext } from "../../Context/AuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

export default function Source() {
  const [sources, setSources] = useState([]);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [formVisible, setFormVisible] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [uploadVisible, setUploadVisible] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const menu = useRef(null);
  const toast = useRef(null);
  const { auth } = useContext(AuthContext);
  const token = auth?.token;

  const items = [{ label: "SOURCE" }];
  const home = { icon: "pi pi-home", url: "/" };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [sourcesRes, productsRes] = await Promise.all([getSources(token), getProducts(token)]);
      setSources(sourcesRes.data.data || []);
      setProducts(productsRes.data.data || []);
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch data" });
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) fetchData(); }, [token, fetchData]);

  const textEditor = options => (
    <input
      type="text"
      value={options.value}
      onChange={e => options.editorCallback(e.target.value)}
      disabled={!options.rowData.is_enabled}
    />
  );

  const productEditor = options => {
    const { rowData, value, editorCallback } = options;
    const selectedIds = Array.isArray(value) ? value.map(v => typeof v === "object" ? v.id : v) : rowData.Products?.map(p => p.id) || [];
    return (
      <MultiSelect
        value={selectedIds}
        options={products.map(p => ({ label: p.name, value: p.id }))}
        onChange={e => editorCallback(e.value)}
        display="chip"
        placeholder="Select Products"
        disabled={!rowData.is_enabled}
      />
    );
  };

  const onCellEditComplete = e => {
    const { rowData, newValue, field } = e;
    if (!rowData.is_enabled) {
      toast.current?.show({ severity: "warn", summary: "Action Forbidden", detail: "This source is disabled and cannot be edited." });
      return;
    }
    if (newValue === undefined || newValue === null) return;

    const payload = {
      key_id: field === "key_id" ? newValue : rowData.key_id,
      product_ids: field === "Products"
        ? Array.isArray(newValue)
          ? newValue.map(v => (typeof v === "object" ? v.id : v))
          : []
        : rowData.Products?.map(p => p.id) || []
    };

    const hasChanges = payload.key_id !== rowData.key_id || payload.product_ids.toString() !== rowData.Products?.map(p => p.id).toString();
    if (!hasChanges) return;

    confirmDialog({
      message: "Are you sure you want to update this source?",
      header: "Confirm Update",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        setIsLoading(true);
        try {
          const res = await updateSource(rowData.id, payload, token);
          setSources(prev => prev.map(s => s.id === rowData.id ? res.data.data : s));
          toast.current?.show({ severity: "success", summary: "Updated", detail: "Source updated" });
        } catch {
          toast.current?.show({ severity: "error", summary: "Error", detail: "Update failed" });
        } finally { setIsLoading(false); }
      }
    });
  };

  const handleDisable = sourceIds => {
    if (!sourceIds.length) return;
    confirmDialog({
      message: "Are you sure you want to toggle the status of selected source(s)?",
      header: "Confirm Status Toggle",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        setIsLoading(true);
        try {
          const updated = await Promise.all(sourceIds.map(async id => {
            const source = sources.find(s => s.id === id);
            const res = await toggleSource(id, !source.is_enabled, token);
            return res.data.data;
          }));
          setSources(prev => prev.map(s => updated.find(u => u.id === s.id) || s));
          setSelected([]);
          toast.current?.show({ severity: "success", summary: "Status Updated", detail: "Source status updated" });
        } catch {
          toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to update status" });
        } finally { setIsLoading(false); }
      }
    });
  };

  const rowClassName = rowData => ({ "opacity-50": !rowData.is_enabled, "cursor-not-allowed": !rowData.is_enabled });

  const filteredSources = useMemo(() => {
    if (!filterValue) return sources;
    return sources.filter(s => `${s.key_id}`.toLowerCase().includes(filterValue.toLowerCase()));
  }, [sources, filterValue]);

  const sourceFilterElement = () => {
    const options = sources.map(s => ({ label: s.key_id, value: s.key_id }));
    return (
      <Dropdown
        value={filterValue}
        options={options}
        editable
        placeholder="Search Source"
        onChange={e => setFilterValue(e.value || "")}
        panelStyle={{ maxWidth: "220px" }}
      />
    );
  };

  const filterEnabledSources = data => data.filter(s => s.is_enabled);

  const downloadJSON = () => {
    if (selected.some(s => !s.is_enabled)) {
      toast.current?.show({ severity: "warn", summary: "Action Forbidden", detail: "Disabled sources cannot be downloaded." });
      return;
    }
    const data = selected.length ? selected : filterEnabledSources(sources);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "sources.json";
    link.click();
  };

  const downloadExcel = () => {
    if (selected.some(s => !s.is_enabled)) {
      toast.current?.show({ severity: "warn", summary: "Action Forbidden", detail: "Disabled sources cannot be downloaded." });
      return;
    }
    const data = selected.length ? selected : filterEnabledSources(sources);
    const worksheet = XLSX.utils.json_to_sheet(
      data.map(d => ({ key_id: d.key_id, products: d.Products?.map(p => p.name).join(", ") || "" }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sources");
    XLSX.writeFile(workbook, "sources.xlsx");
  };

  const downloadPDF = () => {
    if (selected.some(s => !s.is_enabled)) {
      toast.current?.show({ severity: "warn", summary: "Action Forbidden", detail: "Disabled sources cannot be downloaded." });
      return;
    }
    const data = selected.length ? selected : filterEnabledSources(sources);
    const doc = new jsPDF();
    doc.text("Sources", 14, 20);
    autoTable(doc, { head: [["Key ID", "Products"]], body: data.map(d => [d.key_id, d.Products?.map(p => p.name).join(", ") || ""]), startY: 30 });
    doc.save("sources.pdf");
  };

  const downloadTemplateJSON = () => {
    const template = [{ key_id: "example_source_key", product_ids: [1, 2] }];
    const blob = new Blob([JSON.stringify(template, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "source_template.json";
    link.click();
  };

const downloadTemplateExcel = () => {
  setTimeout(() => {
    const template = [{ key_id: "example_source_key", products: "Product1, Product2" }];
    const worksheet = XLSX.utils.json_to_sheet(template);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, "source_template.xlsx");
  }, 50);
};

  const renderProducts = row => (
    <div className="flex flex-wrap gap-1">
      {row.Products?.map(p => (
        <span key={p.id} style={{ backgroundColor: "#FC5F1CFF", color: "#fff", padding: "2px 8px", borderRadius: "12px", fontSize: "0.85rem" }}>{p.name}</span>
      ))}
    </div>
  );

  const handleDeleteSelected = () => {
  if (!selected.length) return;

  confirmDialog({
    message: `Are you sure you want to delete the selected ${selected.length} source(s)?`,
    header: "Confirm Delete",
    icon: "pi pi-exclamation-triangle",
    accept: async () => {
      setIsLoading(true);
      try {
        await deleteSource(
          selected.map(s => s.id),
          token
        );

        await fetchData();
        setSelected([]);

        toast.current?.show({
          severity: "success",
          summary: "Deleted",
          detail: "Sources deleted successfully"
        });

      } catch (err) {
        toast.current?.show({
          severity: "error",
          summary: "Deletion Failed",
          detail:
            err?.response?.data?.message ||
            "Delete failed. No sources were deleted."
        });
      } finally {
        setIsLoading(false);
      }
    }
  });
};


  const handleFormSubmit = async formData => {
    setIsLoading(true);
    try {
      if (editingSource) {
        const res = await updateSource(editingSource.id, formData, token);
        setSources(prev => prev.map(s => s.id === editingSource.id ? res.data.data : s));
      } else {
        const res = await createSource(formData, token);
        setSources(prev => [...prev, res.data.data]);
      }
      setFormVisible(false);
      setEditingSource(null);
      toast.current?.show({ severity: "success", summary: "Success", detail: "Operation completed" });
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Error", detail: err?.response?.data?.message || "Operation failed" });
    } finally { setIsLoading(false); }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {isLoading && (
        <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(255,255,255,0.8)", zIndex: 10, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <ProgressSpinner />
        </div>
      )}
      <Toast ref={toast} />
      <ConfirmDialog />

      <h2 className="mb-1 text-3xl font-bold" style={{ color: "#FC5F1CFF" }}>SOURCE</h2>
      <BreadCrumb model={items} home={home} style={{ background: "transparent", border: "none" }} />

      <DataTable
        value={filteredSources.slice(first, first + rows)}
        dataKey="id"
        selection={selected}
        onSelectionChange={e => setSelected(e.value)}
        editMode="cell"
        showGridlines
        rowClassName={rowClassName}
        header={
          <div className="flex justify-content-end gap-2">
            <Button 
              icon="pi pi-plus" 
              rounded 
              tooltip="Add" 
              onClick={() => setFormVisible(true)} 
              style={{ background: "#FC5F1CFF", border: "none" }} 
            />
            <Button 
              icon="pi pi-file" 
              rounded 
              tooltip="JSON" 
              onClick={downloadJSON} 
              style={{ background: "#FC5F1CFF", border: "none" }} 
            />
            <Button 
              icon="pi pi-file-excel" 
              rounded 
              tooltip="Excel" 
              onClick={downloadExcel} 
              style={{ background: "#FC5F1CFF", border: "none" }} 
            />
            <Button 
              icon="pi pi-file-pdf" 
              rounded 
              tooltip="PDF" 
              onClick={downloadPDF} 
              style={{ background: "#FC5F1CFF", border: "none" }} 
            />
            <Button 
              icon="pi pi-upload" 
              rounded 
              tooltip="Upload" 
              onClick={() => setUploadVisible(true)} 
              style={{ background: "#FC5F1CFF", border: "none" }} 
            />
            <Button 
              icon="pi pi-ban"
              rounded 
              tooltip="Disable" 
              disabled={!selected.length} 
              onClick={() => handleDisable(selected.map(s => s.id))} 
              style={{ background: "#FC5F1CFF", border: "none" }} 
            />
            <Button 
              icon="pi pi-trash" 
              rounded 
              tooltip="Delete" 
              disabled={!selected.length} 
              onClick={handleDeleteSelected} 
              style={{ background: "#FF4B4B", border: "none" }} 
            />
            <Button 
              icon="pi pi-ellipsis-v" 
              rounded 
              tooltip="Template"
              style={{ background: "#FC5F1CFF", border: "none" }} 
              onClick={e => menu.current.toggle(e)} 
            />
            <Menu ref={menu} popup model={[
              { label: "Download JSON Template", icon: "pi pi-file", command: downloadTemplateJSON },
              { label: "Download Excel Template", icon: "pi pi-file-excel", command: downloadTemplateExcel }
            ]} />
          </div>
        }
        footer={
          <Paginator first={first} rows={rows} totalRecords={filteredSources.length} rowsPerPageOptions={[10, 20, 30]} onPageChange={e => { setFirst(e.first); setRows(e.rows); }} />
        }
      >
        <Column selectionMode="multiple" style={{ width: "3rem" }} />
        <Column field="key_id" header="Source Key" editor={textEditor} onCellEditComplete={onCellEditComplete} filter filterElement={sourceFilterElement} />
        <Column field="Products" header="Products" editor={productEditor} onCellEditComplete={onCellEditComplete} body={renderProducts} />
      </DataTable>

      <SourceForm visible={formVisible} onHide={() => { setFormVisible(false); setEditingSource(null); }} onSubmit={handleFormSubmit} initialData={editingSource} products={products} />

      <SourceForm visible={uploadVisible} uploadMode products={products} onHide={() => setUploadVisible(false)} onSubmit={async data => {
        confirmDialog({
          message: "Are you sure you want to upload these sources?",
          header: "Confirm Upload",
          icon: "pi pi-exclamation-triangle",
          accept: async () => {
            setIsLoading(true);
            try {
              const createdSources = [];
              for (const s of data) {
                const res = await createSource(s, token);
                createdSources.push(res.data.data);
              }
              setSources(prev => [...prev, ...createdSources]);
              setUploadVisible(false);
              toast.current?.show({ severity: "success", summary: "Uploaded", detail: "Sources uploaded successfully" });
            } catch (err) {
              toast.current?.show({ severity: "error", summary: "Error", detail: err?.response?.data?.message || err.message });
            } finally { setIsLoading(false); }
          }
        });
      }} />
    </div>
  );
}
